import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { FONT_STACK, colors } from '../theme.js';

/**
 * Il riscontro breve in basso (03-SCHERMATE-TV §1.4): «Aggiunto ai preferiti», «Promemoria attivo».
 *
 * ## Perché serve su un televisore più che altrove
 *
 * Perché il gesto non ha un ritorno. Su un telefono si tocca una stella e la stella si accende sotto
 * il dito; a tre metri, con un telecomando, si preme OK e **si guarda dall'altra parte dello schermo**
 * — la riga del canale è in basso, il fuoco è in alto. Senza un riscontro si preme due volte, e la
 * seconda disfa la prima.
 *
 * ## Due secondi e due decimi, e non è un numero a caso
 *
 * È il prototipo, e regge la prova pratica: abbastanza da leggersi, abbastanza poco da non coprire
 * la fascia sotto mentre ci si muove. Un riscontro che resta cinque secondi diventa un ostacolo.
 *
 * ## Non è un canale per gli errori
 *
 * Un errore va dove sta il problema — nel lettore, nel form, sulla riga — e ci resta finché non è
 * risolto. Un errore che sparisce dopo due secondi è un errore che nessuno ha letto: qui ci vanno
 * solo i **fatti compiuti**.
 */

export const TOAST_MS = 2_200;

interface ToastApi {
  /** Mostra un riscontro. Il testo arriva già tradotto: questo componente non conosce l'i18n. */
  readonly show: (message: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

/**
 * Chi vuole mostrare un riscontro.
 *
 * Fuori dal provider **non esplode**: restituisce un'interfaccia che non fa niente. Un riscontro è
 * una cortesia, e far cadere una schermata perché manca il contenitore sarebbe scambiare l'accessorio
 * per la funzione — per esempio nelle prove del DOM, che montano una schermata sola.
 */
export function useToast(): ToastApi {
  return useContext(ToastContext) ?? SILENT;
}

const SILENT: ToastApi = { show: () => undefined };

export function ToastProvider({ children }: { children: ReactNode }): ReactNode {
  const [message, setMessage] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const show = useCallback((next: string) => {
    /*
      Il secondo riscontro **sostituisce** il primo, e ne riparte il tempo.

      Chi aggiunge tre canali ai preferiti in fila non deve vedere tre riquadri in coda, né il primo
      che sparisce mentre il terzo comincia: si vede sempre l'ultima cosa fatta, che è quella a cui
      si sta pensando.
    */
    if (timer.current !== null) clearTimeout(timer.current);
    setMessage(next);
    timer.current = setTimeout(() => {
      setMessage(null);
      timer.current = null;
    }, TOAST_MS);
  }, []);

  // Smontando si spegne il tempo: un `setState` su un componente che non c'è più è un avviso in
  // console e, su un runtime vecchio, un riferimento che resta appeso.
  useEffect(() => {
    return () => {
      if (timer.current !== null) clearTimeout(timer.current);
    };
  }, []);

  return (
    <ToastContext.Provider value={{ show }}>
      {children}
      {message !== null && (
        <div
          data-toast="1"
          /*
            `aria-live`: su un televisore con la sintesi vocale accesa il riscontro va **detto**, e
            questa è l'unica cosa che lo rende possibile. Senza, l'unico modo di sapere che il canale
            è stato aggiunto è vederlo.
          */
          aria-live="polite"
          style={{
            position: 'fixed',
            left: '50%',
            bottom: 72,
            transform: 'translateX(-50%)',
            zIndex: 90,
            minHeight: 72,
            maxWidth: 900,
            padding: '18px 34px',
            borderRadius: 14,
            background: colors.surfaceRaised,
            color: colors.text,
            font: `700 26px ${FONT_STACK}`,
            display: 'flex',
            alignItems: 'center',
            // Non si può premere e non ruba il fuoco: è un riscontro, non un comando.
            pointerEvents: 'none',
            boxShadow: 'var(--sh, 0 18px 48px rgba(0,0,0,.45))',
          }}
        >
          {message}
        </div>
      )}
    </ToastContext.Provider>
  );
}
